/**
 * Tracks rows that are mid-removal so a list can show a "completed" overlay
 * and exit animation for a short dwell before the row actually disappears,
 * instead of popping it out of the list immediately. Shared by any feature
 * that needs this graceful removal behaviour (e.g. live scorecards list,
 * all-matches list) so the dwell/cleanup bookkeeping isn't duplicated per
 * consumer.
 */
export class RowRemovalAnimator {
  constructor(private readonly holdDurationMs: number = 900) {}

  /** Row ids currently showing the removal overlay — read by templates. */
  readonly removingRowIds = new Set<string>();

  private readonly pendingTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * @param holdDurationMs - How long a row stays marked as "removing" (and
   *   visible with the overlay) before `onSettle` is invoked.
   */
  /**
   * Marks `rowId` as removing (idempotent — re-entrant calls for the same id
   * are ignored), then invokes `onSettle` once the hold duration elapses so
   * the caller can splice/filter the row out of its own list.
   *
   * @param rowId - Stable row identity (`__row_id`).
   * @param onSettle - Invoked after the dwell to perform the actual removal.
   */
  remove(rowId: string, onSettle: () => void): void {
    if (!rowId || this.removingRowIds.has(rowId)) {
      return;
    }

    this.removingRowIds.add(rowId);

    const timeoutId = setTimeout(() => {
      this.pendingTimeouts.delete(rowId);
      this.removingRowIds.delete(rowId);
      onSettle();
    }, this.holdDurationMs);
    this.pendingTimeouts.set(rowId, timeoutId);
  }

  /**
   * Cancels a pending removal for `rowId` without invoking `onSettle` — use
   * when a row that was mid-dwell gets revived (shown again) before its
   * removal timer fires, so it doesn't get spliced out later and doesn't
   * keep showing the removal overlay.
   *
   * @param rowId - Stable row identity (`__row_id`).
   */
  cancel(rowId: string): void {
    const timeoutId = this.pendingTimeouts.get(rowId);
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      this.pendingTimeouts.delete(rowId);
    }
    this.removingRowIds.delete(rowId);
  }

  /** Clears pending removal timers and overlay state — call from ngOnDestroy. */
  destroy(): void {
    this.pendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.pendingTimeouts.clear();
    this.removingRowIds.clear();
  }
}
